import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ToastContainer, toast } from "react-toastify";
import InputMask from "react-input-mask";
import { useFormik } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
} from "reactstrap";
import DeleteModal from "../../Components/Common/DeleteModal";
import TableContainer from "../../Components/Common/TableContainer";
import Loader from "../../Components/Common/Loader";
import { getSellersQuery, getUsersQuery } from "../../queries";
import "react-toastify/dist/ReactToastify.css";
import { createSeller, editSeller, deleteSeller } from "../../api/seller";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Sellers = () => {
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [seller, setseller] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const toggleEdit = useCallback(() => {
    setEditModal(!editModal);
    if (editModal) {
      setseller(null);
    }
  }, [editModal]);

  const [userSearchInput, setUserSearchInput] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserOption, setSelectedUserOption] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => setUserSearch(userSearchInput), 500);
    return () => clearTimeout(handler);
  }, [userSearchInput]);

  const { data: users } = useQuery(
    getUsersQuery({
      page: 1,
      size: 20,
      ...(userSearch
        ? /^\+?\d+$/.test(userSearch)
          ? { containsPhoneNumber: userSearch.replace(/\+/g, "") }
          : { containsFirstName: userSearch }
        : {}),
    })
  );
  const { data: sellers } = useQuery(getSellersQuery({ page, size: 1000 }));

  const userOptions = useMemo(() => {
    if (!users?.data) return [];

    const fetchedOptions = users.data.map((u) => ({
      value: u.id,
      label: `${u.firstName} ${u.lastName} (${u.phoneNumber})`,
    }));

    if (selectedUserOption) {
      const exists = fetchedOptions.some((opt) => opt.value === selectedUserOption.value);
      if (!exists) {
        fetchedOptions.unshift(selectedUserOption);
      }
    }

    if (isEdit && seller?.user) {
      const exists = fetchedOptions.some((opt) => opt.value === seller.userId);
      if (!exists) {
        fetchedOptions.unshift({
          value: seller.userId,
          label: `${seller.user.firstName} ${seller.user.lastName} (${seller.user.phoneNumber})`,
        });
      }
    }

    return fetchedOptions;
  }, [users?.data, isEdit, seller, selectedUserOption]);

  const filteredSellers = useMemo(() => {
    return (
      sellers?.data?.data?.filter(
        (s) => s.user?.firstName && s.user?.lastName
      ) || []
    );
  }, [sellers?.data?.data]);

  const toggle = useCallback(() => {
    setModal(!modal);
    if (!modal) {
      setseller(null);
      setSelectedUserOption(null);
    }
  }, [modal]);

  console.log(seller);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      userId: seller?.userId || "",
      cardNumbers: seller?.cardNumbers || [{ cardNumber: "", expireDate: "" }],
      identifiers: seller?.identifiers || [
        { type: "PhoneNumber", value: "998" },
        { type: "INN", value: "" },
        { type: "PassportSerialNumber", value: "" },
        { type: "PINFL", value: "" },
        { type: "AccountCredit", value: "" },
        { type: "MFO", value: "" },
      ],
      status: seller?.status || "active",
      comment: seller?.comment || "",
    },
    validationSchema: Yup.object({
      userId: Yup.number().required(t("Please select a User")),
      status: Yup.string().required(t("Please select a Status")),
    }),
    onSubmit: (values) => {
      const sanitizedValues = {
        ...values,
        cardNumbers: values.cardNumbers.map((card) => ({
          cardNumber: card.cardNumber.replace(/\s/g, ""), // Remove spaces
          expireDate: card.expireDate.replace("/", ""), // Convert MM/YY to MMYY
        })),
        identifiers: values.identifiers.map((identifier) => ({
          type: identifier.type,
          value: identifier.value.replace(/\s/g, ""), // Remove spaces
        })),
        comment: values.comment.trim(), // Trim spaces from comment
      };

      const apiCall = isEdit
        ? editSeller(sanitizedValues, seller.id)
        : createSeller(sanitizedValues);

      apiCall
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["sellers"] });
          toggle();
        })
        .catch((err) => {
          if (err?.data.error?.message || err?.data?.error?.code) {
            alert(err?.data.error?.message || err?.data?.error?.code);
          }
        });
    },
  });

  const editValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: seller?.status || "active",
      comment: seller?.comment || "",
    },
    validationSchema: Yup.object({
      status: Yup.string().required(t("Please select a Status")),
    }),
    onSubmit: (values) => {
      editSeller(
        {
          status: values.status,
          comment: values.comment ? values.comment.trim() : "",
        },
        seller.id
      )
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["sellers"] });
          toggleEdit();
          toast.success(t("Seller updated successfully"));
        })
        .catch((err) => {
          if (err?.data?.error?.message || err?.data?.error?.code) {
            alert(err?.data.error?.message || err?.data?.error?.code);
          }
        });
    },
  });

  const handleEdit = (sellerItem) => {
    setseller(sellerItem);
    setEditModal(true);
  };

  const onDelete = () => {
    if (deleteModal) {
      deleteSeller(deleteModal)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["sellers"] });
          setDeleteModal(false);
          toast.success(t("Seller deleted successfully"));
        })
        .catch((err) => {
          toast.error(err?.response?.data?.error?.message || t("Error deleting seller"));
          setDeleteModal(false);
        });
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t("First name"),
        cell: ({ row }) => row.original.user?.firstName || "-",
      },
      {
        header: t("Last name"),
        cell: ({ row }) => row.original.user?.lastName || "-",
      },
      {
        header: t("Phone"),
        cell: ({ row }) => row.original.user?.phoneNumber || "-",
      },
      {
        header: t("Card"),
        cell: ({ row }) => row.original.cardNumbers?.[0]?.maskedNumber,
      },
      {
        header: t("Status"),
        cell: ({ row }) => {
          const status = row.original.status || "active";
          return status === "active" ? (
            <span className="badge bg-success-subtle text-success text-uppercase">
              {t("Active")}
            </span>
          ) : (
            <span className="badge bg-danger-subtle text-danger text-uppercase">
              {t("Blocked")}
            </span>
          );
        },
      },
      {
        header: t("Actions"),
        cell: ({ row }) => (
          <>
            <Link
              to={`/sellers/${row.original.id}`}
              className="text-primary d-inline-block"
            >
              <button className="btn btn-sm btn-primary">
                <i className="ri-eye-fill"></i>
              </button>
            </Link>
            <button
              className="btn btn-sm btn-warning"
              style={{ marginLeft: "10px" }}
              onClick={() => handleEdit(row.original)}
            >
              <i className="ri-pencil-fill"></i>
            </button>
            <button
              className="btn btn-sm btn-danger"
              style={{ marginLeft: "10px" }}
              onClick={() => setDeleteModal(row.original.id)}
            >
              <i className="ri-delete-bin-6-fill"></i>
            </button>
          </>
        ),
      },
    ],
    [t]
  );

  return (
    <div className="page-content">
      <DeleteModal
        show={deleteModal}
        onDeleteClick={onDelete}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card id="sellerList">
              <CardHeader className="border-0 ">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="mb-0 fw-bold">{t("Sellers")}</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-success add-btn"
                        id="create-btn"
                        onClick={() => {
                          setIsEdit(false);
                          toggle();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>{" "}
                        {t("create")}
                      </button>
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <div className="py-2"></div>
              <div className="card-body pt-0">
                {sellers?.data?.data.length ? (
                  <TableContainer

                    columns={columns}
                    data={filteredSellers}
                    customPageSize={1000}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-1"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light text-muted"
                  />
                ) : (
                  <Loader />
                )}
                <Modal isOpen={modal} toggle={toggle} centered size="lg">
                  <ModalHeader toggle={toggle}>
                    {isEdit ? t("edit") : t("create")}
                  </ModalHeader>
                  <Form onSubmit={validation.handleSubmit}>
                    <ModalBody>
                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="userId" className="form-label">
                              {t("User")}
                            </Label>
                            <Select
                              name="userId"
                              id="userId"
                              value={
                                userOptions.find(
                                  (opt) => opt.value === validation.values.userId
                                ) || null
                              }
                              onChange={(selectedOption) => {
                                validation.setFieldValue(
                                  "userId",
                                  selectedOption ? selectedOption.value : ""
                                );
                                setSelectedUserOption(selectedOption);
                              }}
                              onBlur={() => validation.setFieldTouched("userId", true)}
                              options={userOptions}
                              onInputChange={(newValue) => {
                                setUserSearchInput(newValue);
                              }}
                              placeholder={t("Select a user")}
                              isClearable
                              classNamePrefix="react-select"
                            />
                            {validation.touched.userId && validation.errors.userId && (
                              <div className="text-danger mt-1">
                                {validation.errors.userId}
                              </div>
                            )}
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-3">
                            <Label>{t("Status")}</Label>
                            <Input
                              type="select"
                              value={validation.values.status}
                              onChange={validation.handleChange}
                              name="status"
                            >
                              <option value="active">{t("Active")}</option>
                              <option value="blocked">{t("Blocked")}</option>
                            </Input>
                          </div>
                        </Col>
                      </Row>

                      <div className="mb-1 mt-3">
                        <Label
                          className="form-label"
                          style={{ fontSize: "16px" }}
                        >
                          - {t("Cards")}
                        </Label>
                      </div>
                      {validation.values.cardNumbers.map((card, index) => (
                        <Row key={index}>
                          <Col md={6} className="mb-3">
                            <Label>{t("Card number")}</Label>
                            <InputMask
                              mask="9999 9999 9999 9999"
                              value={card.cardNumber}
                              onChange={(e) => {
                                const newCards = [
                                  ...validation.values.cardNumbers,
                                ];
                                newCards[index].cardNumber = e.target.value;
                                validation.setFieldValue(
                                  "cardNumbers",
                                  newCards
                                );
                              }}
                            >
                              {(inputProps) => (
                                <Input {...inputProps} type="text" />
                              )}
                            </InputMask>
                          </Col>
                          <Col md={6}>
                            <Label>{t("Expire Date")}</Label>
                            <InputMask
                              mask="99/99"
                              value={card.expireDate}
                              onChange={(e) => {
                                const newCards = [
                                  ...validation.values.cardNumbers,
                                ];
                                newCards[index].expireDate = e.target.value;
                                validation.setFieldValue(
                                  "cardNumbers",
                                  newCards
                                );
                              }}
                            >
                              {(inputProps) => (
                                <Input {...inputProps} type="text" />
                              )}
                            </InputMask>
                          </Col>
                        </Row>
                      ))}
                      <button
                        className="btn btn-sm btn-primary mb-3"
                        type="button"
                        onClick={() =>
                          validation.setFieldValue("cardNumbers", [
                            ...validation.values.cardNumbers,
                            { cardNumber: "", expireDate: "" },
                          ])
                        }
                      >
                        + {t("Add Another Card")}
                      </button>

                      <div className="mb-1 mt-3">
                        <Label
                          className="form-label"
                          style={{ fontSize: "16px" }}
                        >
                          - {t("Identifiers")}
                        </Label>
                      </div>
                      <Row>
                        {validation.values.identifiers.map(
                          (identifier, index) => (
                            <Col md={6} key={index}>
                              <div className="mb-3">
                                <Label>{t(identifier.type)}</Label>
                                <InputMask
                                  mask={
                                    identifier.type === "PhoneNumber"
                                      ? "999 99 999 99 99"
                                      : identifier.type === "INN"
                                        ? "999999999"
                                        : identifier.type ===
                                          "PassportSerialNumber"
                                          ? "aa9999999"
                                          : identifier.type === "PINFL"
                                            ? "99999999999999"
                                            : identifier.type === "AccountCredit"
                                              ? "99999999999999999999"
                                              : identifier.type === "MFO"
                                                ? "99999"
                                                : ""
                                  }
                                  value={identifier.value}
                                  onChange={(e) => {
                                    const newIdentifiers = [
                                      ...validation.values.identifiers,
                                    ];
                                    newIdentifiers[index].value =
                                      e.target.value;
                                    validation.setFieldValue(
                                      "identifiers",
                                      newIdentifiers
                                    );
                                  }}
                                >
                                  {(inputProps) => (
                                    <Input {...inputProps} type="text" />
                                  )}
                                </InputMask>
                              </div>
                            </Col>
                          )
                        )}
                      </Row>
                    </ModalBody>

                    <ModalFooter>
                      <button className="btn btn-light" onClick={toggle}>
                        {t("Close")}
                      </button>
                      <button className="btn btn-success" type="submit">
                        {t("create")}
                      </button>
                    </ModalFooter>
                  </Form>
                </Modal>

                <Modal isOpen={editModal} toggle={toggleEdit} centered size="lg">
                  <ModalHeader toggle={toggleEdit}>
                    {t("edit")}
                  </ModalHeader>
                  <Form onSubmit={editValidation.handleSubmit}>
                    <ModalBody>
                      <Row>
                        <Col md={12}>
                          <div className="mb-3">
                            <Label>{t("Status")}</Label>
                            <Input
                              type="select"
                              value={editValidation.values.status}
                              onChange={editValidation.handleChange}
                              name="status"
                            >
                              <option value="active">{t("Active")}</option>
                              <option value="blocked">{t("Blocked")}</option>
                            </Input>
                          </div>
                          <div className="mb-3">
                            <Label htmlFor="edit-comment" className="form-label">
                              {t("Comment")}
                            </Label>
                            <Input
                              type="textarea"
                              id="edit-comment"
                              name="comment"
                              rows="3"
                              value={editValidation.values.comment}
                              onChange={editValidation.handleChange}
                              placeholder={t("Comment")}
                            />
                          </div>
                        </Col>
                      </Row>
                    </ModalBody>
                    <ModalFooter>
                      <button type="button" className="btn btn-light" onClick={toggleEdit}>
                        {t("Close")}
                      </button>
                      <button className="btn btn-success" type="submit">
                        {t("Update")}
                      </button>
                    </ModalFooter>
                  </Form>
                </Modal>

                <ToastContainer closeButton={false} limit={1} />
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Sellers;
